<template>
  <div class="relative inline-block text-left" v-click-outside="closeMenu">
    <!-- Trigger Button -->
    <button
      @click="toggleMenu"
      type="button"
      class="inline-flex items-center justify-between w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500 transition-colors"
      id="org-menu-button"
      aria-expanded="true"
      aria-haspopup="true"
    >
      <div class="flex items-center min-w-0">
        <OrganizationAvatar
          v-if="currentOrganization"
          :organization="currentOrganization"
          size="sm"
          class="mr-3 flex-shrink-0"
        />
        <div
          v-else
          class="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg mr-3 flex-shrink-0 animate-pulse"
        ></div>
        <span class="truncate">{{ currentOrganization?.name || 'Select Organization' }}</span>
      </div>
      <!-- Chevron icon -->
      <svg
        class="ml-2 -mr-1 h-5 w-5 flex-shrink-0 text-gray-400"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </button>

    <!-- Dropdown menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 dark:divide-gray-700 focus:outline-none z-50"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="org-menu-button"
        tabindex="-1"
      >
        <div class="px-4 py-3" role="none">
          <p
            class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            role="none"
          >
            Your Organizations
          </p>
        </div>
        <div class="py-1 max-h-60 overflow-y-auto" role="none">
          <button
            v-for="org in organizations"
            :key="org.id"
            @click="selectOrganization(org.id)"
            class="group flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            :class="{
              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400':
                org.id === currentOrganizationId,
            }"
            role="menuitem"
            tabindex="-1"
          >
            <OrganizationAvatar :organization="org" size="sm" class="mr-3" />
            <span class="flex-1 truncate text-left">{{ org.name }}</span>
            <svg
              v-if="org.id === currentOrganizationId"
              class="ml-2 h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div class="py-1" role="none">
          <button
            @click="$emit('create')"
            class="group flex items-center w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            role="menuitem"
            tabindex="-1"
          >
            <div
              class="w-8 h-8 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center mr-3 group-hover:border-blue-500 group-hover:text-blue-500"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <span>Create New Organization</span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useOrganizationContext } from '../composables';
  import { clickOutside as vClickOutside } from '@/directives/click-outside';
  import OrganizationAvatar from './OrganizationAvatar.vue';

  const emit = defineEmits<{
    (e: 'create'): void;
    (e: 'change', id: string): void;
  }>();

  const {
    organizations,
    currentOrganizationId,
    currentOrganization,
    setCurrentOrganization,
    initialize,
  } = useOrganizationContext();

  const isOpen = ref(false);

  const toggleMenu = () => {
    isOpen.value = !isOpen.value;
  };

  const closeMenu = () => {
    isOpen.value = false;
  };

  const selectOrganization = (id: string) => {
    setCurrentOrganization(id);
    isOpen.value = false;
    emit('change', id);
  };

  onMounted(async () => {
    await initialize();
  });
</script>
